/**
 * src/lib/pricing.ts
 * Single source of truth for package prices and total computation.
 * Used by both server-side routes (reserve, create-payment-intent) and
 * the client-side BookingEngine for display purposes.
 *
 * IMPORTANT: The authoritative total is always computed server-side.
 * The client imports these constants only for display purposes.
 */

export type PackageId = 'pkg_first_exp' | 'pkg_4h' | 'pkg_10h';

/** Base prices in euros (whole numbers) */
export const PACKAGE_PRICES: Record<PackageId, number> = {
  pkg_first_exp: 120,
  pkg_4h: 380,
  pkg_10h: 800,
};

/** Video add-on price in euros */
export const VIDEO_ADDON_PRICE = 45;

/** 2-person bundle discount for pkg_first_exp in euros */
export const FIRST_EXP_BUNDLE_DISCOUNT = 20;

export interface ComputeTotalParams {
  packageId: PackageId;
  partySize: number;
  addVideo: boolean;
}

/**
 * Compute the authoritative total in cents.
 *
 * Rules:
 * - pkg_4h: base is flat (€380), regardless of party size
 * - other packages: base = price × partySize
 * - pkg_first_exp + partySize === 2: apply −€20 bundle discount
 * - addVideo: add €45
 *
 * Returns total in cents (integer).
 */
export function computeTotalCents({ packageId, partySize, addVideo }: ComputeTotalParams): number {
  const basePrice = PACKAGE_PRICES[packageId];

  let subtotal: number;
  if (packageId === 'pkg_4h') {
    // Flat price for the 4h couple package
    subtotal = basePrice;
  } else {
    subtotal = basePrice * partySize;
  }

  // 2-person bundle discount for Prima Esperienza
  if (packageId === 'pkg_first_exp' && partySize === 2) {
    subtotal -= FIRST_EXP_BUNDLE_DISCOUNT;
  }

  // Video add-on
  if (addVideo) {
    subtotal += VIDEO_ADDON_PRICE;
  }

  return subtotal * 100; // convert to cents
}

export interface ComputeDiscountParams {
  baseCents: number;
  discountType: 'percent' | 'fixed';
  discountValue: number;
  maxDiscountCents?: number | null;
}

/**
 * Compute the discount amount in cents.
 * - 'percent': discountValue is a percentage (0–100, decimals allowed e.g. 9.1).
 *   Result is rounded to the nearest whole euro (Math.round(cents / 100) * 100)
 *   so amounts are always tidy. Then capped at maxDiscountCents if provided.
 * - 'fixed': discountValue is already in cents; returns Math.min(discountValue, baseCents).
 *   maxDiscountCents is not applied to fixed codes (the value is already explicit).
 * The discount never exceeds the base total.
 */
export function computeDiscountCents({ baseCents, discountType, discountValue, maxDiscountCents }: ComputeDiscountParams): number {
  if (discountType === 'percent') {
    // discountValue may be a decimal (e.g. 9.1 for 9.1%)
    const rawCents = baseCents * discountValue / 100;
    // Round to nearest whole euro for tidy amounts
    let discount = Math.round(rawCents / 100) * 100;
    // Apply optional max cap
    if (maxDiscountCents != null && maxDiscountCents > 0) {
      discount = Math.min(discount, maxDiscountCents);
    }
    // Never exceed base
    return Math.min(discount, baseCents);
  }
  // fixed: discountValue is stored in cents in the DB
  return Math.min(discountValue, baseCents);
}
